import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { showErrorDialog } from '../shared/utils';
import { AvatarCardUser, BasicUser } from './basic-user.model';
import { LocalStorageService } from 'ngx-localstorage';
import { GRAPH_API } from '../shared/constants';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private apiEndpoint = `${environment.apiEndpoint}/api/static/users`;
  private portalApiEndpoint = environment.cigpPortalApi.endpoint
    ? `${environment.cigpPortalApi.endpoint}/api/users`
    : null;

  private joiningDates: { [key: string]: moment.Moment } = {};
  private joiningDates$: { [key: string]: Observable<moment.Moment | null> } = {};

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    public apiErrorDialog: MatDialog,
    private storage: LocalStorageService
  ) {}

  getCurrentUserCorporateTitle$(): Observable<string> {
    return this.http
      .get(`${this.apiEndpoint}/current/corporate-title`, {
        responseType: 'text'
      })
      .pipe(
        map((title: string) => title),
        catchError((error: any) => {
          this.logger.error(error);
          showErrorDialog(this.apiErrorDialog, error);
          return throwError(() => new Error(error));
        })
      );
  }

  public getOne$(email: string): Observable<BasicUser> {
    return this.http.get<BasicUser>(`${this.apiEndpoint}/${email}`);
  }

  public getMe$(): Observable<BasicUser | null> {
    return this.http.get<any>(`${GRAPH_API.endpoint}/me`).pipe(
      map((me) => ({
        email: me.mail || me.userPrincipalName,
        name: me.displayName,
        title: me.jobTitle || ''
      })),
      catchError((error: any) => {
        this.logger.error(error);
        showErrorDialog(this.apiErrorDialog, error);
        return throwError(() => new Error(error));
      })
    );
  }

  public getMyAvatarCardInfo$(): Observable<AvatarCardUser | null> {
    const cachedAvatarCardInfo = this.storage.get<AvatarCardUser>('myAvatarCardInfo');
    if (cachedAvatarCardInfo) {
      return of(cachedAvatarCardInfo);
    } else if (this.portalApiEndpoint) {
      return this.http.get<AvatarCardUser>(`${this.portalApiEndpoint}/me`).pipe(
        tap((info) => this.storage.set('myAvatarCardInfo', info)),
        catchError((error: any) => {
          this.logger.error(error);
          return throwError(() => new Error(error));
        })
      );
    } else {
      return this.getMe$().pipe(
        map((user) =>
          user
            ? {
                email: user.email,
                initials: user.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase(),
                name: user.name,
                profilePicture: null
              }
            : null
        )
      );
    }
  }

  public getJoiningDate$(email: string): Observable<moment.Moment | null> {
    if (this.joiningDates[email]) {
      return of(this.joiningDates[email]);
    }

    if (!this.joiningDates$[email]) {
      this.joiningDates$[email] = this.apiEndpoint
        ? this.http
            .get(`${this.portalApiEndpoint}/${email}/joined`, {
              responseType: 'text'
            })
            .pipe(
              map((joined) => (joined ? moment(joined.replace(/"/g, '')) : null)),
              tap((joined) => {
                if (joined) {
                  this.joiningDates[email] = joined;
                }
              }),
              catchError((error: any) => {
                this.logger.error(error);
                return of(moment('2000-01-01T00:00:00Z'));
              })
            )
        : of(null);
    }

    return this.joiningDates$[email];
  }
}
